"use client";

import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, KeyRound } from "lucide-react";
import BaseDialog from "@/components/Modals/BaseDialog";
import { CopyButton } from "@/components/UI/CopyButton";

interface SendLogsResult {
  password: string;
  filename: string;
}

export const SendLogsToSupportDialog = ({
  isOpen,
  onOpenChange,
  groupId,
  runId,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  runId: string;
}) => {
  const sendMutation = useMutation<SendLogsResult>({
    mutationFn: async () => {
      const response = await fetch(
        `/api/backend/groups/${groupId}/runs/${runId}/send-logs-to-support`,
        { method: "POST" }
      );
      if (!response.ok) {
        let detail = "Failed to send logs to support.";
        try {
          const body = await response.json();
          detail = body.detail || body.message || detail;
        } catch {
          // keep the generic message
        }
        throw new Error(detail);
      }
      return response.json();
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (!open) sendMutation.reset();
    onOpenChange(open);
  };

  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      size="lg"
      title="Send logs to support"
      footerActions={
        sendMutation.data
          ? [
              {
                label: "Done",
                props: { onClick: () => handleOpenChange(false) },
              },
            ]
          : [
              {
                label: "Cancel",
                props: {
                  variant: "outline",
                  onClick: () => handleOpenChange(false),
                  disabled: sendMutation.isPending,
                },
              },
              {
                label: sendMutation.isPending
                  ? "Sending..."
                  : "Send to support",
                props: {
                  onClick: () => sendMutation.mutate(),
                  disabled: sendMutation.isPending,
                },
              },
            ]
      }
    >
      {sendMutation.data ? (
        <div className="space-y-3">
          <p className="text-sm">
            The logs were sent to support as{" "}
            <span className="font-mono">{sendMutation.data.filename}</span>.
          </p>
          <div className="rounded-md border p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              <KeyRound className="h-4 w-4" />
              Archive password
            </div>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                {sendMutation.data.password}
              </code>
              <CopyButton value={sendMutation.data.password} iconSize={14} />
            </div>
            <p className="text-xs text-muted-foreground">
              Support cannot open the archive without this password. It is shown
              only once. Share it with support through your support channel.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-md border border-yellow-500/40 bg-yellow-500/10 p-3 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            <div>
              This sends the run&apos;s execution logs and an export of the
              workflow to Noxus support. Workflows can contain sensitive details
              such as prompts, configuration, and data references, so make sure
              you are allowed to share them. Embedded file contents are masked
              before sending.
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            The data is packed into a password-protected archive. You will
            receive the password after sending; support cannot open the archive
            without it.
          </p>
          {sendMutation.isError && (
            <p className="text-sm text-destructive">
              {sendMutation.error instanceof Error
                ? sendMutation.error.message
                : "Failed to send logs to support."}{" "}
              Please try again.
            </p>
          )}
        </div>
      )}
    </BaseDialog>
  );
};
